/**
 * The things a tech does on a job, written once for both states of the world.
 *
 * Every one of these tries the server first and falls back to the queue when
 * it cannot be reached. That order matters: online, the server is authoritative
 * — it assigns the document number, it decides whether a variation is approved,
 * it enforces the gate — and taking its answer is better than guessing. Offline,
 * the queue takes it and the device applies the same rules locally so the tech
 * sees the same thing they will see when it syncs.
 *
 * The rule that makes this safe is that the device never decides anything the
 * server owns. A variation raised here is *predicted*, not approved: the
 * prediction runs the identical pure function the server will (D18), so the
 * tech can tell the customer what is going to happen, and the server still
 * makes it happen.
 *
 * What a caller gets back is `{ result, queued }`. `queued` is not a detail to
 * swallow — "saved" and "saved on this phone, waiting to send" are different
 * things to tell someone standing in front of a customer.
 */

import { ApiError, api } from "./api";
import { standing, submit } from "./variations";
import * as outbox from "./outbox";
import * as store from "./store";
import { get, type Row } from "./store";
import * as repo from "./repo";
import type { CommissionResult, JobDetailOut } from "./types";

export type Outcome<T> = {
  result: T;
  /** True when this is held on the device rather than accepted by the server. */
  queued: boolean;
  /** Written for a person, when there is something worth saying. */
  message?: string;
};

/** Only a transport failure falls back to the queue. */
function isUnreachable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/**
 * A 409 is the server refusing on business grounds — the variation gate, a
 * pending approval. That is an answer, not an outage, and queueing it would
 * mean the device quietly retrying something the server has already said no
 * to. It is rethrown so the screen can show the reason.
 */
function rethrowIfAnswered(error: unknown): never | void {
  if (!isUnreachable(error)) throw error;
}

/* ------------------------------------------------------------------ jobs */

export async function arrive(jobId: string): Promise<Outcome<JobDetailOut | null>> {
  const at = new Date().toISOString();
  try {
    return { result: await api.jobs.arrive(jobId), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.update("job", jobId, { arrived_at: at, status: "on_site" });
    return {
      result: await repo.job(jobId),
      queued: true,
      message: "Arrival recorded on this phone. It'll send when you're back in range.",
    };
  }
}

/**
 * What was done on site, and the customer's order number — the words the
 * invoice and the docket print. Written on the phone as often as in the
 * office, so it queues like everything else a tech does.
 */
export async function writeUp(
  jobId: string,
  fields: { work_done?: string; purchase_order_number?: string },
): Promise<Outcome<JobDetailOut | null>> {
  try {
    const result = await api.jobs.writeUp(jobId, fields);
    await store.patch("job", jobId, fields);
    return { result, queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.update("job", jobId, fields);
    return { result: await repo.job(jobId), queued: true, message: "Saved on this phone. It'll send when you're back in range." };
  }
}

export async function addLine(
  jobId: string,
  line: Record<string, unknown>,
): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await api.jobs.addLine(jobId, line), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
    await outbox.create("job_line", { ...line, job_id: jobId });
    return { result: await repo.job(jobId), queued: true, message: "Part added on this phone." };
  }
}

/**
 * Book time, and refuse it here if the server would.
 *
 * The gate is checked locally before queueing, because the whole point of it
 * is that the conversation happens while the tech is on site. Queueing time
 * the server is going to reject, and telling them about it that evening, is
 * the failure the gate exists to prevent — only later and with less chance of
 * fixing it.
 */
export async function bookTime(
  jobId: string,
  entry: { minutes: number; user_id: string; kind?: string; billable?: boolean },
): Promise<Outcome<JobDetailOut | null>> {
  try {
    return { result: await api.jobs.bookTime(jobId, entry), queued: false };
  } catch (error) {
    rethrowIfAnswered(error);

    const blocked = await wouldBeBlocked(jobId, entry.minutes / 60);
    if (blocked) {
      throw new ApiError(409, blocked, { block: "local", detail: blocked });
    }

    const user = await get<Row>("user_account", entry.user_id);
    await outbox.create("time_entry", {
      job_id: jobId,
      user_id: entry.user_id,
      kind: entry.kind ?? "on_site",
      started_at: new Date().toISOString(),
      minutes: entry.minutes,
      billable: entry.billable ?? true,
      // Snapshotted here, as the server does: a rate change tomorrow must not
      // rewrite what today's hours cost.
      cost_cents: Number(user?.hourly_cost_cents ?? 0),
      charge_cents: Number(user?.hourly_charge_cents ?? 0),
    });

    return { result: await repo.job(jobId), queued: true, message: "Time booked on this phone." };
  }
}

/** The same check the server runs, against what this device is holding. */
async function wouldBeBlocked(jobId: string, proposedHours: number): Promise<string | null> {
  const job = await repo.job(jobId);
  if (!job) return null;

  const rows = await repo.variations(jobId);
  const where = standing(
    rows.map((v) => ({
      status: String(v.status ?? "draft"),
      amount_cents: Number(v.amount_cents ?? 0),
      hours: Number(v.hours ?? 0),
    })),
    {
      quoted_hours: job.quoted_hours,
      hours_actual: job.costing?.hours_actual ?? 0,
      overrun_accepted_hours: job.overrun_accepted_hours,
      proposed_hours: proposedHours,
    },
  );

  return where.block === "none" ? null : (where.warnings[0] ?? "This job needs approval before more time.");
}

/* ------------------------------------------------------------ variations */

export type RaisedVariation = {
  id: string;
  status: string;
  message: string;
  needs_office: boolean;
  /** True when this is the device's prediction rather than the server's ruling. */
  predicted: boolean;
};

/**
 * Raise a variation, and tell the tech what it means.
 *
 * Offline the outcome is *predicted* by running the same pure function the
 * server will (D18). The prediction needs the org's field approval limit,
 * which the working set carries — and if it does not, the honest answer is
 * "waiting on the office" rather than a guess that might have the tech telling
 * a customer their $4,000 variation is approved.
 */
export async function raiseVariation(
  jobId: string,
  payload: {
    description: string;
    amount_cents: number;
    hours?: number | null;
    customer_signature_url?: string | null;
    accepted_by_name?: string | null;
    submit?: boolean;
  },
): Promise<Outcome<RaisedVariation>> {
  try {
    const response = await api.variations.raise(jobId, payload);
    return {
      result: {
        id: response.variation.id,
        status: response.variation.status,
        message: response.message,
        needs_office: response.needs_office,
        predicted: false,
      },
      queued: false,
    };
  } catch (error) {
    rethrowIfAnswered(error);

    const orgs = await repo.org();
    const limit = orgs === null ? 0 : Number(orgs.field_approval_limit_cents ?? 0);
    const outcome =
      payload.submit === false
        ? { status: "draft", needs_office: false, reason: "Saved as a draft." }
        : submit({
            amount_cents: payload.amount_cents,
            field_approval_limit_cents: limit,
            has_signature: Boolean(payload.customer_signature_url),
          });

    const row = await outbox.create("variation", {
      job_id: jobId,
      description: payload.description,
      amount_cents: payload.amount_cents,
      hours: payload.hours ?? null,
      customer_signature_url: payload.customer_signature_url ?? null,
      accepted_by_name: payload.accepted_by_name ?? null,
      // Local only: `status` is not in the sync whitelist, so this is what the
      // screen shows until the server rules on it. It is never sent.
      status: outcome.status,
    }, { localOnly: ["status"] });

    return {
      result: {
        id: row.id,
        status: outcome.status,
        message: outcome.reason,
        needs_office: outcome.needs_office,
        predicted: true,
      },
      queued: true,
    };
  }
}


/* ---------------------------------------------------------------- quotes */

export type SavedQuote = {
  id: string;
  /** The number the customer was shown: real if online, provisional if not. */
  number: string;
  total_cents: number;
  predicted: boolean;
};

export type QuoteDraft = {
  customer_id: string;
  site_id: string;
  title: string;
  target_margin_bp: number;
  sections: {
    kit_id: string;
    title: string;
    driver_qty: number;
    include_optional_item_ids?: string[];
  }[];
};

/**
 * Save a quote built at a site walk.
 *
 * Online this is one call and the server does the expansion. Offline it is a
 * three-level create — quote, sections, lines — assembled here and queued. The
 * projector ranks creates so a section cannot land before its quote and a line
 * cannot land before its section (D14), which is what makes sending all three
 * in one batch safe.
 *
 * Two things the device does *not* decide, and both matter:
 *
 *  * **The number.** A provisional `Q-D4-0003` is minted so the customer can
 *    be handed something, and the server swaps in the real one at sync while
 *    keeping the provisional in `client_ref` (D10).
 *  * **The totals.** `cost_cents` and friends are not writable from a device.
 *    The device prices the quote so it can show a number on site; the server
 *    prices it again when the batch lands. Those are the same number because
 *    both pricing engines are held to the same fixtures (D23) — which is the
 *    entire reason that work was worth doing.
 */
export async function saveQuote(draft: QuoteDraft): Promise<Outcome<SavedQuote>> {
  try {
    const quote = await api.quotes.create({
      customer_id: draft.customer_id,
      site_id: draft.site_id,
      title: draft.title,
      target_margin_bp: draft.target_margin_bp,
      built_on_site: true,
      sections: draft.sections.map((s) => ({
        kit_id: s.kit_id,
        title: s.title,
        driver_qty: s.driver_qty,
        include_optional_item_ids: s.include_optional_item_ids ?? [],
      })),
    });
    return {
      result: { id: quote.id, number: quote.number, total_cents: quote.total_cents, predicted: false },
      queued: false,
    };
  } catch (error) {
    rethrowIfAnswered(error);
    return queueQuote(draft);
  }
}

async function queueQuote(draft: QuoteDraft): Promise<Outcome<SavedQuote>> {
  const quote = await outbox.create(
    "quote",
    {
      customer_id: draft.customer_id,
      site_id: draft.site_id,
      title: draft.title,
      target_margin_bp: draft.target_margin_bp,
      status: "draft",
      built_on_site: true,
    },
    { numberPrefix: "Q", localOnly: ["status"] },
  );

  let total = 0;

  for (const [sort, section] of draft.sections.entries()) {
    // Expanded here rather than trusting whatever the screen had in hand: the
    // preview is a view, and a quote is a record. Re-running the expansion
    // means what is stored is what the rules say, not what a component held.
    const preview = await repo.kitPreview(section.kit_id, section.driver_qty, {
      targetMarginBp: draft.target_margin_bp,
      includeOptional: new Set(section.include_optional_item_ids ?? []),
    });
    if (!preview) continue;
    total += preview.totals.total_cents;

    const row = await outbox.create("quote_section", {
      quote_id: quote.id,
      kit_id: section.kit_id,
      title: section.title,
      driver_qty: section.driver_qty,
      is_optional: false,
      sort,
    });

    for (const [lineSort, line] of preview.lines.entries()) {
      await outbox.create("quote_line", {
        section_id: row.id,
        // Both of these were missing until ASSET-3's browser pass: the line
        // priced correctly, so nothing looked wrong, but acceptance keys on
        // them. Without `catalogue_item_id` a quote built in the car park
        // reserved no stock, drafted no PO and gave the job lines nothing to
        // take off the van; without `creates_asset` it pre-registered not
        // one camera. Every quote built offline, silently (D48).
        catalogue_item_id: line.item_id,
        creates_asset: line.creates_asset,
        description: line.description,
        kind: line.kind,
        qty: line.qty,
        uom: line.uom,
        unit_cost_cents: line.unit_cost_cents,
        unit_sell_cents: line.unit_sell_cents,
        sort: lineSort,
      });
    }
  }

  // Stored locally so the quote shows its own total before it has synced.
  // Not queued — see the note above about who owns the money columns.
  await store.patch("quote", quote.id, { total_cents: total });

  return {
    result: {
      id: quote.id,
      number: String(quote.client_ref ?? "—"),
      total_cents: total,
      predicted: true,
    },
    queued: true,
    message: `Saved on this phone as ${String(quote.client_ref)}. It'll send when you're back in range.`,
  };
}

/* ---------------------------------------------------------------- assets */

export type ScanDetails = {
  location_desc?: string;
  firmware?: string;
  ip_address?: string;
};

/** Trim what a scanner adds and a human types — the server's `normalise`. */
export function normaliseSerial(raw: string): string {
  return raw.split(/\s+/).filter(Boolean).join(" ");
}

/**
 * Scan a serial onto an asset shell.
 *
 * Online, the server does everything: duplicate check across the whole
 * register, install date, warranty recounted from the real term. Offline —
 * which is most of the time, because this happens in a plant room — the
 * device queues the *fact* as an append-only `installed` event and the server
 * derives the same consequences from it when the phone finds signal (D47).
 *
 * Two things the phone can do on its own, and does:
 *
 *  * refuse a serial it can already see on a different asset. That is only
 *    the assets this device holds, not the whole register, but it catches the
 *    common mistake — the box of the camera next to it — while the tech is
 *    still standing there. The server catches the rest.
 *  * show the serial on the asset straight away, so the worklist shrinks as
 *    they go. That is a local patch, not a queued write: the server does not
 *    let a device set a serial directly, and would refuse it.
 *
 * What it does not do is guess the warranty. It does not know the term, and a
 * made-up date on a screen is a date somebody reads out to a customer.
 */
export async function commission(
  assetId: string,
  rawSerial: string,
  details: ScanDetails & { job_id?: string | null } = {},
): Promise<Outcome<CommissionResult | null>> {
  const serial = normaliseSerial(rawSerial);
  if (!serial) throw new ApiError(422, "Scan or type the serial first.");

  // A scan the server refused for this asset is answered by this one, whatever
  // happens next: the tech has seen the reason and is fixing it. Left in the
  // queue, it would keep this asset showing as refused after a scan that
  // worked.
  const answered = async () => {
    const stale = (await outbox.rejected()).filter(
      (c) =>
        c.entity === "asset_event" &&
        (c.value as Record<string, unknown> | null)?.asset_id === assetId &&
        (c.value as Record<string, unknown> | null)?.kind === "installed",
    );
    if (stale.length) await outbox.acknowledgeRejected(stale.map((c) => c.seq!).filter((n) => n !== undefined));
  };

  try {
    const result = await api.assets.commission(assetId, { serial, ...details });
    await answered();
    // The server's version of the row, onto the phone now rather than at the
    // next pull — otherwise the worklist does not shrink after a scan that
    // worked, and the tech scans it again to be sure.
    await store.patch("asset", assetId, { ...result.asset, pending_install: false });
    return { result, queued: false };
  } catch (error) {
    rethrowIfAnswered(error);
  }

  const here = await get<Row>("asset", assetId);
  const known = await store.all<Row>("asset");
  // A serial the server already refused is not one this phone should hold
  // against the next asset — that is the box they are now trying correctly.
  const states = await repo.installStates();
  const clash = known.find(
    (a) =>
      a.id !== assetId &&
      !a.deleted_at &&
      states.get(a.id)?.state !== "refused" &&
      typeof a.serial === "string" &&
      a.serial.toLowerCase() === serial.toLowerCase(),
  );
  if (clash) {
    throw new ApiError(
      409,
      `Serial ${serial} is already on ${String(clash.ref)}. Check you have the right box.`,
    );
  }
  if (
    states.get(assetId)?.state !== "refused" &&
    typeof here?.serial === "string" &&
    here.serial.toLowerCase() === serial.toLowerCase()
  ) {
    return { result: null, queued: false, message: `${String(here.ref)} is already this serial — nothing changed.` };
  }

  const payload = Object.fromEntries(
    Object.entries({ serial, ...details }).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  await answered();
  await outbox.create("asset_event", {
    asset_id: assetId,
    job_id: details.job_id ?? here?.installed_by_job_id ?? null,
    kind: "installed",
    occurred_at: new Date().toISOString(),
    summary: `Scanned ${serial}`,
    payload,
  });
  await store.patch("asset", assetId, {
    serial,
    ...(details.location_desc ? { location_desc: details.location_desc } : {}),
    pending_install: true,
  });

  return {
    result: null,
    queued: true,
    message: `${String(here?.ref ?? "Asset")} scanned on this phone. The warranty date is worked out when it sends.`,
  };
}
